import { useState, useMemo } from 'react';

export type SortDirection = 'asc' | 'desc';

export interface SortColumn {
  field: string;
  direction: SortDirection;
}

export interface UseTableSortProps<T> {
  data: T[];
  initialSort?: SortColumn[];
}

export interface UseTableSortReturn<T> {
  sortedData: T[];
  sortColumns: SortColumn[];
  handleSort: (field: string, ctrlKey?: boolean) => void;
  getSortDirection: (field: string) => SortDirection | null;
  getSortIndex: (field: string) => number;
}

export function useTableSort<T extends Record<string, any>>({
  data,
  initialSort = []
}: UseTableSortProps<T>): UseTableSortReturn<T> {
  const [sortColumns, setSortColumns] = useState<SortColumn[]>(initialSort);

  // Check if a field represents a date/time column
  const isDateField = (field: string): boolean => {
    const lower = field.toLowerCase();
    return lower.includes('date') || lower.includes('time') || lower.includes('asof');
  };

  const handleSort = (field: string, ctrlKey = false) => {
    // Date columns default to descending (newest first), others to ascending (A-Z)
    const defaultDirection: SortDirection = isDateField(field) ? 'desc' : 'asc';

    setSortColumns(prevSort => {
      const existingIndex = prevSort.findIndex(col => col.field === field);

      if (!ctrlKey) {
        // Single column sort - replace all sorting
        if (existingIndex >= 0) {
          // Toggle direction if same column
          const currentDirection = prevSort[existingIndex].direction;
          const newDirection: SortDirection = currentDirection === 'asc' ? 'desc' : 'asc';
          return [{ field, direction: newDirection }];
        } else {
          // New column, start with default direction for field type
          return [{ field, direction: defaultDirection }];
        }
      } else {
        // Multi-column sort - add/modify/remove
        if (existingIndex >= 0) {
          // Column already in sort - toggle direction or remove
          const currentDirection = prevSort[existingIndex].direction;
          const otherDirection: SortDirection = currentDirection === 'asc' ? 'desc' : 'asc';
          if (currentDirection === defaultDirection) {
            // Change to opposite direction
            const newSort = [...prevSort];
            newSort[existingIndex] = { field, direction: otherDirection };
            return newSort;
          } else {
            // Remove from sort
            return prevSort.filter(col => col.field !== field);
          }
        } else {
          // Add new column to sort with default direction
          return [...prevSort, { field, direction: defaultDirection }];
        }
      }
    });
  };

  const getSortDirection = (field: string): SortDirection | null => {
    const sortCol = sortColumns.find(col => col.field === field);
    return sortCol ? sortCol.direction : null;
  };

  const getSortIndex = (field: string): number => {
    const index = sortColumns.findIndex(col => col.field === field);
    return index >= 0 ? index + 1 : 0; // Return 1-based index, 0 if not sorted
  };

  const sortedData = useMemo(() => {
    if (sortColumns.length === 0) return data;

    return [...data].sort((a, b) => {
      for (const { field, direction } of sortColumns) {
        let aVal = a[field];
        let bVal = b[field];

        // Handle null/undefined values
        if (aVal == null && bVal == null) continue;
        if (aVal == null) return direction === 'asc' ? -1 : 1;
        if (bVal == null) return direction === 'asc' ? 1 : -1;

        // Convert to comparable values
        if (typeof aVal === 'string' && typeof bVal === 'string') {
          aVal = aVal.toLowerCase();
          bVal = bVal.toLowerCase();
        }

        // Handle dates
        if (isDateField(field)) {
          aVal = new Date(aVal).getTime();
          bVal = new Date(bVal).getTime();
        }

        // Compare values
        let comparison = 0;
        if (aVal < bVal) comparison = -1;
        else if (aVal > bVal) comparison = 1;

        if (comparison !== 0) {
          return direction === 'asc' ? comparison : -comparison;
        }
      }
      return 0;
    });
  }, [data, sortColumns]);

  return {
    sortedData,
    sortColumns,
    handleSort,
    getSortDirection,
    getSortIndex
  };
}
